import Box from '@mui/material/Box';
import { useAppInitialization } from '@src/components/app/useAppInitialization';
import { useAppPdfGeneration } from '@src/components/app/useAppPdfGeneration';
import FocusModeGradient from '@src/components/common/FocusModeGradient';
import FocusModeImage from '@src/components/common/FocusModeImage';
import MobileControlArea from '@src/components/common/MobileControlArea';
import Navigation from '@src/components/common/Navigation';
import PdfPreviewDrawer from '@src/components/common/PdfPreviewDrawer';
import SkipNavigation from '@src/components/common/SkipNavigation';
// 開発環境のみで開発者コントロールパネルをインポート
import DevControlPanel from '@src/components/dev/DevControlPanel';
import { ToastContainer } from '@src/components/toast/ToastContainer';
import { setupTypingWatcher } from '@src/modules/app/appHelpers';
import { storeReduxStateToLocalStorage } from '@src/modules/local_storage_utils';
import { useTranslation } from '@src/modules/react_hooks/useTranslation';
import store from '@src/store/index';
import { selectFocusMode } from '@src/store/slices/focus_mode';
import { updateRefreshPdf } from '@src/store/slices/ui';
import { memo, type ReactElement, useCallback, useEffect, useId } from 'react';
import { useSelector } from 'react-redux';
import { DesktopView } from './layouts/DesktopView';
import { MobileView } from './layouts/MobileView';
import { TabletView } from './layouts/TabletView';

/**
 * メインアプリケーションコンポーネント
 *
 * アプリケーションのルートコンポーネントです。
 * レスポンシブデザインに対応し、PCとモバイルで異なるレイアウトを提供します。
 * PDF生成機能とメッセージ表示機能を統合しています。
 *
 * @returns {ReactElement} メインアプリケーションコンポーネント
 *
 * @example
 * ```tsx
 * <App />
 * ```
 *
 * フォーカスモード対応を追加
 * フォーカスモード状態に応じてテーマとCSSクラスを切り替えます
 *
 * @since 1.0.0
 * @author Resume Commit Team
 */
function App(): ReactElement {
  // Variables
  const mainContentId = useId();
  const { t } = useTranslation();

  // Initialization Hooks
  const { isDesktopView, isTabletView, isScrolling, isInputActive } = useAppInitialization();

  const isFocusMode = useSelector(selectFocusMode) || false;

  // PDF Generation Hooks
  const {
    pdfPage,
    pdfPageTotal,
    setPdfPageTotal,
    isPreviewDrawerOpen,
    isPdfScrolling,
    setIsPdfScrolling,
    previewToken,
    isPreviewGenerating,
    setIsPreviewGenerating,
    shouldShowPreview,
    handlePdfPageChange,
    handlePreviewToggle,
    handlePreviewClose,
  } = useAppPdfGeneration(isDesktopView);

  /**
   * タイピング監視の設定ハンドラー
   *
   * デバウンス機能付きのタイピング監視を設定し、
   * ローカルストレージへの状態保存、タイポチェック完了待機、PDF更新を順番に実行します。
   *
   * @returns {() => void} クリーンアップ関数
   */
  const handleTypingWatcher = useCallback((): (() => void) => {
    const cleanup = setupTypingWatcher(async () => {
      // ローカルストレージにアプリケーションの状態を保持
      storeReduxStateToLocalStorage(store.getState());

      // タイポチェックが有効で実行中の場合は完了を待つ
      const state = store.getState();
      if (state.typoCheck.enabled && state.typoCheck.runningCount > 0) {
        // タイポチェック完了を待機（最大3秒）
        let waitTime = 0;
        const maxWaitTime = 3000;
        const checkInterval = 100;

        while (waitTime < maxWaitTime) {
          const currentState = store.getState();
          if (currentState.typoCheck.runningCount === 0) {
            break;
          }
          await new Promise((resolve) => setTimeout(resolve, checkInterval));
          waitTime += checkInterval;
        }
      }

      // PDF更新をトリガー
      store.dispatch(updateRefreshPdf(true));
    });

    return cleanup;
  }, []);

  // 最適化されたタイピング監視の設定
  useEffect(() => {
    const cleanup = handleTypingWatcher();
    return cleanup;
  }, [handleTypingWatcher]);

  return (
    <>
      <SkipNavigation mainContentId="main-content" />
      <Navigation />
      {isFocusMode && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
            pointerEvents: 'none',
          }}
        >
          <FocusModeImage isVisible={true} />
        </Box>
      )}
      <FocusModeGradient isVisible={isFocusMode} />
      <Box
        component="main"
        id={mainContentId}
        aria-label={t('accessibility.mainContent')}
        flex={1}
        minHeight="calc(100vh - 256px)"
        sx={{
          marginTop: { xs: '28px', sm: '32px' },
          position: 'relative',
          zIndex: 1,
        }}
      >
        {isDesktopView ? (
          <DesktopView
            preview={shouldShowPreview}
            isScrolling={isScrolling}
            isInputActive={isInputActive}
            pdfPage={pdfPage}
            onPdfPageChange={handlePdfPageChange}
            onPdfPageTotalChange={setPdfPageTotal}
          />
        ) : isTabletView ? (
          <TabletView />
        ) : (
          <MobileView />
        )}
      </Box>
      {import.meta.env.DEV && <DevControlPanel />}
      {!isDesktopView && (
        <>
          <MobileControlArea
            isScrolling={isScrolling || isPdfScrolling}
            isInputActive={isInputActive}
            isPreviewMode={isPreviewDrawerOpen}
            isPreviewGenerating={isPreviewGenerating}
            page={pdfPage}
            pageTotal={pdfPageTotal}
            onPageChange={handlePdfPageChange}
            onPreviewToggle={handlePreviewToggle}
          />
          <PdfPreviewDrawer
            open={isPreviewDrawerOpen}
            onClose={handlePreviewClose}
            isScrolling={isScrolling}
            page={pdfPage}
            onPageChange={handlePdfPageChange}
            onPageTotalChange={setPdfPageTotal}
            onPdfScrollingChange={setIsPdfScrolling}
            onPreviewGeneratingChange={setIsPreviewGenerating}
            previewToken={previewToken}
          />
        </>
      )}
      <ToastContainer position="bottom-right" />
    </>
  );
}

export default memo(App);
